import { useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { toast } from "react-hot-toast";
import { useApplicationFormLogic } from "../../hooks/useApplicationFormLogic";
import useCurrentUser from "../../hooks/useCurrentUser";
import ApplicationFormFooter from "../../pages/Application/ApplicationFormFooter";
import EvaluationInput from "../../pages/EvaluationNew/components/EvaluationInput";
import { request } from "../../utils/api";
import { getLocalSystemDate, parseIsoDateToMDYUS } from "../../utils/dateUtils";
import { mergeFormDataWithStatusExtras, applyIsoDatesToFields } from "../../utils/applicationFormPayload";

import EvaluationFormHeader from "../../pages/EvaluationNew/components/EvaluationFormHeader";
import CloseButton from "./CloseButton";
import CorrectionRequestAlert from "./CorrectionRequestAlert";
import { resolveFormTenantId } from "../../utils/tenantContext";

const BaseProtocol = ({
  role,
  items,
  apiEndpoint = "/job-descriptions", // Default endpoint
  defaultValues = {},
  onClose,
  targetUserId: targetUserIdProp, // Accept the prop passed from parent
}) => {

  const [loading, setLoading] = useState(false);
  const [currentStatus, setCurrentStatus] = useState("");
  const [dataId, setDataId] = useState(null);
  const { user: currentUserData, seniorStaffNurse } = useCurrentUser()

  const methods = useForm({
    defaultValues: {
      tenantId: undefined,
      userId: targetUserIdProp ?? undefined,
      applicantName: "",
      applicantSignature: "",
      applicantSignedAt: getLocalSystemDate(),
      applicantStatementAccepted: false,
      reviewedBySignedBy: "",
      reviewedBySignature: "",
      reviewedBySignedAt: "",
      name: "",
      ...defaultValues,
    },
    mode: "onChange",
  });

  const onSubmit = async (data, flag = 'send_to_office', additionalData = {}) => {
    // Ensure flag is a string
    const actualFlag = typeof flag === 'string' ? flag : 'send_to_office';
    try {
      const merged = mergeFormDataWithStatusExtras(data, additionalData);
      const payload = {
        tenantId: resolveFormTenantId(
          Number.parseInt(merged.tenantId, 10) || currentUserData?.tenantId,
        ),
        userId: Number.parseInt(merged.userId || targetUserIdProp, 10) || currentUserData?.id,
        name:
          merged.name ||
          merged.applicantName ||
          merged.employeeName ||
          merged.signerNameDisplay ||
          (currentUserData
            ? `${currentUserData.firstName} ${currentUserData.lastName}`
            : ""),
        role: (role || merged.role || "").toUpperCase(),
        dateOfEmployment: merged.dateOfEmployment, // If exists in some protocols
        // Map the checkbox field back to the backend field
        applicantStatementAccepted: !!merged.applicantStatementAccepted,
        applicantSignature: merged.applicantSignature,
        applicantSignedBy: merged.applicantSignedBy,
        reviewedBySignature: merged.reviewedBySignature,
        reviewedBySignedBy: merged.reviewedBySignedBy || "",
        ...applyIsoDatesToFields(merged, ["applicantSignedAt", "reviewedBySignedAt"]),
        flag: actualFlag,
        comments: merged.comments || "",
      };

      if (!payload.role) {
        toast.error("Role is required.");
        return;
      }
      if (!payload.name) {
        toast.error("Name is required.");
        return;
      }

      toast.loading("Submitting Document...", {
        id: "protocol-submit",
      });

      const actualId = dataId || merged.id;
      const method = actualId ? "PUT" : "POST";
      const url = actualId ? `${apiEndpoint}/${actualId}` : apiEndpoint;

      const response = await request(url, {
        method,
        body: JSON.stringify(payload),
      });

      if (response.status === 1) {
        toast.success(response.message || "Document submitted successfully!", {
          id: "protocol-submit",
        });
        closeForm()
      } else {
        toast.error(response.message || "Failed to submit document!", {
          id: "protocol-submit",
        });
      }
    } catch (error) {
      console.error("Error submitting document:", error);
      toast.error(`Error: ${error.message}`, { id: "protocol-submit" });
    }
  };

  const {
    targetUserId: capturedUserId, // from search params (= employee user id when admin views)
    isSignatureModalOpen,
    openSignatureModal,
    closeSignatureModal,
    handleSignatureApply,
    handleStatusAction,
    handleSave,
    signerName,
    isReadOnly,
    closeForm,
    actualFromId
  } = useApplicationFormLogic({
    methods,
    applicantNameField: "applicantName",
    applicantSignatureDateField: "applicantSignedAt",
    onClose,
    onSubmit,
    currentStatus
  });

  const { watch, reset } = methods;

  // Determine the effective userId: prefer URL param, fall back to prop
  const effectiveUserId = capturedUserId || targetUserIdProp;
  useEffect(() => {
    const fetchData = async () => {
      // Need either the record ID (actualFromId) or at least a user to fetch by
      if (!effectiveUserId || !currentUserData) return;

      setLoading(true);
      try {
        const roleId = currentUserData.roleId;
        const isAdminUser = Number(roleId) === 1;

        let response;
        if (actualFromId) {
          // Editing an existing record — fetch by record ID
          if (isAdminUser) {
            response = await request(`${apiEndpoint}/${actualFromId}?userId=${effectiveUserId}`);
          } else {
            response = await request(`${apiEndpoint}/${actualFromId}`);
          }
        } else {
          // New form — try fetching by userId to see if a draft exists
          response = await request(`${apiEndpoint}?userId=${effectiveUserId}`);
        }

        if (response.status === 1) {
          const data = response.result?.jobDescription || response.result?.data || response.result?.rolesAndResponsibilities || response.result;
          setDataId(data.id);
          setCurrentStatus(data.status || "");

          // Map backend fields to form fields
          reset({
            tenantId: resolveFormTenantId(data.tenantId),
            userId: data.userId || effectiveUserId,
            applicantName: data.applicantName || data.name || "",
            applicantSignature: data.applicantSignature || "",
            applicantSignedAt: parseIsoDateToMDYUS(data.applicantSignedAt),
            // 'applicantStatementAccepted' is the RHF field name for the certification checkbox in ApplicationFormFooter
            applicantStatementAccepted: !!data.applicantStatementAccepted,
            // Admin section
            reviewedBySignature: data.reviewedBySignature || data.adminSignature || "",
            reviewedBySignedAt: parseIsoDateToMDYUS(data.reviewedBySignedAt),
            reviewedBySignedBy: data.reviewedBySignedBy || "",
            // Other fields
            name: data.name || data.applicantName || "",
            role: data.role || role || "",
            status: data.status || "",
            comments: data.comments || "",
          });
        }
      } catch (error) {
        console.error("Error fetching protocol data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only form data load
  }, []);

  // Determine who can sign what
  const isAdminUser = Number(currentUserData?.roleId) === 1;
  // Admin signs the "reviewed by" section; employee signs the applicant section
  const canSignEmployee = !isReadOnly && !isAdminUser;
  const canSignAdmin = !isReadOnly && isAdminUser;

  const renderItem = (item) => {
    switch (item.type) {
      case "header":
        return (
          <div
            key={item.id}
            className="mb-8 text-center border-b border-gray-200 pb-4"
          >
            {/* Role Selection if not provided via prop */}
            {!role && (
              <div className="mb-4 text-center">
                <h3 className="text-lg font-bold text-gray-700 mb-2">
                  Select Role <span className="text-red-500">*</span>
                </h3>
                <select
                  {...methods.register("role", {
                    required: "Role is required",
                  })}
                  className="border border-gray-300 rounded px-4 py-2 focus:outline-none focus:border-blue-500 bg-white"
                >
                  <option value="">Select a Role</option>
                  <option value="PT">PT</option>
                  <option value="PTA">PTA</option>
                  <option value="OT">OT</option>
                  <option value="OTA">OTA</option>
                  <option value="ST">ST</option>
                  <option value="RN">RN</option>
                  <option value="LVN">LVN</option>
                  <option value="HHA">HHA</option>
                  <option value="MSW">MSW</option>
                  <option value="CON">CON</option>
                </select>
              </div>
            )}

            {(item.position || item.reportsTo) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto p-6">
                {item.position && (
                  <div>
                    <EvaluationInput
                      label="Position"
                      name="positionDisplay"
                      value={item.position}
                      readOnly={true}
                      disabled={true}
                    />
                  </div>
                )}
                {item.reportsTo && (
                  <div>
                    <EvaluationInput
                      label="Reports To"
                      name="name"
                      placeholder="Enter supervisor's name"
                      value={seniorStaffNurse || item.reportsTo || ""}
                      readOnly={true}
                      disabled={true}
                    />
                  </div>
                )}
              </div>
            )}
            {item.subtitle && (
              <h3 className="text-lg font-bold text-gray-700 mt-2 italic">
                {item.subtitle}
              </h3>
            )}
          </div>
        );
      case "text_block":
        return (
          <div key={item.id} className="mb-6">
            {item.title && (
              <h4 className="text-lg font-bold text-blue-800 mb-2 uppercase">
                {item.title}
              </h4>
            )}
            <p className="text-gray-700 leading-relaxed text-justify whitespace-pre-line">
              {item.text}
            </p>
          </div>
        );
      case "section":
        return (
          <div
            key={item.id}
            className="mb-8 bg-gray-50 p-6 rounded-xl border border-gray-100 shadow-sm"
          >
            <h4 className="text-lg font-bold text-blue-900 mb-4 border-l-4 border-blue-500 pl-4">
              {item.title}
            </h4>
            {item.text && (
              <p className="text-gray-600 mb-4 italic">{item.text}</p>
            )}
            {item.list && (
              <ul className="list-disc pl-6 space-y-2">
                {item.list.map((li, idx) => (
                  <li key={idx} className="text-gray-700 leading-snug">
                    {li}
                  </li>
                ))}
              </ul>
            )}
            {item.duties && (
              <ul className="list-decimal pl-6 space-y-3">
                {item.duties.map((duty, idx) => (
                  <li key={idx} className="text-gray-700 leading-relaxed pl-2">
                    {duty}
                  </li>
                ))}
              </ul>
            )}
            {item.items && ( // Roles and Responsibilities items
              <div className="space-y-4">
                {item.items.map((sub, idx) => (
                  <div key={idx} className="flex gap-4 items-start">
                    <div className="bg-blue-100 text-blue-800 font-bold px-2 py-1 rounded text-xs min-w-[30px] text-center">
                      {sub.number}
                    </div>
                    <div className="flex-1">
                      <p className="text-gray-800">
                        {sub.responsible && (
                          <span className="font-bold text-blue-700 mr-2">
                            [{sub.responsible}]
                          </span>
                        )}
                        {sub.text}
                      </p>
                      {sub.subItems && (
                        <ul className="list-disc pl-6 mt-2 space-y-1">
                          {sub.subItems.map((si, siIdx) => (
                            <li key={siIdx} className="text-gray-600 text-sm">
                              {si}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {item.generalStatement && (
              <p className="mt-4 text-sm text-gray-500 italic text-center">
                {item.generalStatement}
              </p>
            )}
          </div>
        );
      case "protocol_section":
        return (
          <div
            key={item.id}
            className="mb-8 border-2 border-amber-200 bg-amber-50 p-6 rounded-2xl shadow-inner"
          >
            <h4 className="text-xl font-black text-amber-900 mb-4 text-center">
              {item.title}
            </h4>
            <div className="bg-white p-4 rounded-xl shadow-sm mb-4 border border-amber-100">
              <p className="font-bold text-amber-800 mb-1">
                EMAIL TO:{" "}
                <span className="text-blue-600 underline">{item.email}</span>
              </p>
              <p className="font-bold text-amber-800">
                SUBJECT LINE:{" "}
                <span className="text-gray-900 bg-amber-100 px-2 py-0.5 rounded">
                  {item.subject_line}
                </span>
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {item.content.map((c, idx) => (
                <div key={idx} className="bg-white/60 p-4 rounded-lg">
                  <h5 className="font-bold text-blue-900 border-b border-blue-200 mb-2">
                    {c.label}
                  </h5>
                  <p className="text-gray-700 text-sm">{c.text}</p>
                  {c.basic_supplies && (
                    <div className="mt-3">
                      <span className="text-xs font-bold text-gray-500 uppercase tracking-tighter">
                        Basic Supplies:
                      </span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {c.basic_supplies.map((s, sIdx) => (
                          <span
                            key={sIdx}
                            className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-[10px] font-bold border border-blue-100"
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {c.specialty_supplies && (
                    <div className="mt-2">
                      <span className="text-xs font-bold text-red-500 uppercase tracking-tighter">
                        Specialty:
                      </span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {c.specialty_supplies.map((s, sIdx) => (
                          <span
                            key={sIdx}
                            className="bg-red-50 text-red-700 px-2 py-0.5 rounded text-[10px] font-bold border border-red-100"
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="space-y-2">
              <h5 className="font-bold text-amber-900 underline text-sm">
                Rules:
              </h5>
              <ul className="list-none space-y-2">
                {item.rules.map((rule, idx) => (
                  <li
                    key={idx}
                    className="flex gap-3 text-sm text-amber-950 font-medium"
                  >
                    <span className="text-amber-500">▶</span>
                    {rule}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        );
      case "supervisory_section":
        return (
          <div
            key={item.id}
            className="mb-8 border-2 border-blue-200 bg-blue-50 p-6 rounded-2xl shadow-inner"
          >
            <h4 className="text-xl font-black text-blue-900 mb-4 text-center">
              {item.title}
            </h4>
            <ul className="list-none space-y-3">
              {item.rules.map((rule, idx) => (
                <li key={idx} className="flex gap-3 text-gray-800 font-medium">
                  <span className="bg-blue-500 text-white w-5 h-5 rounded-full flex items-center justify-center text-[10px] flex-shrink-0 mt-0.5">
                    !
                  </span>
                  {rule}
                </li>
              ))}
            </ul>
          </div>
        );
      case "exhibit":
        return (
          <div
            key={item.id}
            className="mb-8 bg-white border-2 border-gray-800 p-8 rounded-lg shadow-xl"
          >
            <div className="text-center mb-6">
              <h2 className="text-2xl font-black border-b-4 border-gray-800 inline-block px-8 pb-1 mb-2">
                {item.title}
              </h2>
              <h3 className="text-xl font-bold text-gray-700 uppercase">
                {item.subtitle}
              </h3>
            </div>
            <div className="space-y-6">
              {item.items.map((ex, idx) => (
                <div key={idx} className="flex gap-6 items-start">
                  <div className="text-xl font-black text-gray-400 min-w-[30px]">
                    {ex.number}.
                  </div>
                  <p className="text-gray-800 leading-relaxed text-justify">
                    {ex.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        );
      case "introduction":
        return (
          <p
            key={item.id}
            className="mb-6 text-lg text-gray-700 italic border-l-4 border-gray-200 pl-6 py-2"
          >
            {item.text}
          </p>
        );
      case "footer":
        return (
          <div
            key={item.id}
            className="mt-12 pt-8 border-t border-gray-200 text-center text-gray-400 text-xs italic space-y-1"
          >
            <p>{item.text}</p>
            <p>{item.copyright}</p>
          </div>
        );
      default:
        return null;
    }
  };

  if (loading)
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
      </div>
    );

  return (
    <>
      <CloseButton onClose={closeForm} />
      <div className="max-w-5xl mx-auto bg-white shadow-xl rounded-2xl my-8">
        <EvaluationFormHeader
          title={
            items.find((i) => i.type === "header")?.title || "Job Description"
          }
          subtitle={role ? `Role: ${role}` : "Please select a role"}
          email={currentUserData?.email}
        />

        <div className="p-8">
          <CorrectionRequestAlert status={currentStatus} comments={watch("comments")} />
          <FormProvider {...methods}>
            <form className="relative z-10">
              <fieldset disabled={isReadOnly} className="disabled:opacity-80 disabled:pointer-events-none">

                {items.map((item) => {
                  return renderItem(item);
                })}
              </fieldset>
            </form>


            <ApplicationFormFooter
              page={1}
              setPage={() => { }}
              totalPages={1}
              methods={methods}
              onSave={handleSave}
              onSubmit={(data, status, extra) => onSubmit(data, status, extra)}
              onStatusUpdate={handleStatusAction}
              userRole={currentUserData?.roleId || 1}
              isSignatureModalOpen={isSignatureModalOpen}
              openSignatureModal={openSignatureModal}
              closeSignatureModal={closeSignatureModal}
              handleSignatureApply={handleSignatureApply}
              signerName={signerName}
              isReadOnly={isReadOnly}
              applicantName={watch("applicantName") || watch("name") || ""}
              applicantSignature={watch("applicantSignature") || ""}
              applicantSignedAt={watch("applicantSignedAt") || ""}
              applicantStatementAccepted={watch("applicantStatementAccepted")}
              reviewedBySignedBy={watch("reviewedBySignedBy") || ""}
              reviewedBySignature={watch("reviewedBySignature") || ""}
              reviewedBySignedAt={watch("reviewedBySignedAt") || ""}
              canSignEmployee={canSignEmployee}
              canSignAdmin={canSignAdmin}
              onClose={closeForm}
              currentStatus={currentStatus}
            />
          </FormProvider>
        </div>

      </div>
    </>
  );
};

export default BaseProtocol;
